import React, { useEffect, useState } from 'react';
import store, { loanActions } from '@apply/adminStore';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { RepaymentLevelType, RepaymentExitByTypeOptions } from '../LoanConstants';
import DataGrid, { Column } from 'devextreme-react/data-grid';

export const RepaymentsGrids = ({ propertyId, editable = true }) => {
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [repayments, set_repayments] = useState([]);
    const [isEditable, set_isEditable] = useState(false);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px' },
        formRow: { marginBottom: '0' },
    };

    const loadData = async () => {
        if (!propertyId) {
            return;
        }

        const response = await loanApi.getRepaymentsGrid(propertyId);
        if (response.statusCode !== 200) {
            return;
        }
        set_repayments(response.result.repayments);
        set_isEditable(response.result.isEditable);
    };

    useEffect(() => {
        refreshEvents.addRefreshListener(_afterRefresh);
        return () => {
            refreshEvents.removeRefreshListener(_afterRefresh);
        };
    }, []);

    useEffect(() => {
        // Do we need to await if no code follows?
        async function propertyIdChanged() {
            await loadData();
        }

        propertyIdChanged();
    }, [propertyId]);

    const _afterRefresh = async () => {
        await loadData();
    };

    const editRepayment = (event, repaymentId) => {
        event.preventDefault();
        event.stopPropagation();

        dispatch(
            loanActions.setRepaymentModal({
                repaymentModal: {
                    isOpen: true,
                    entityLevel: RepaymentLevelType.Property,
                    entityId: propertyId,
                    repaymentId: repaymentId,
                },
            })
        );
    };

    const actionCellRender = (e) => {
        if (!editable || !isEditable) {
            return null;
        }

        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='Edit Loan Repayment'
                onClick={(event) => editRepayment(event, e.data.id)}
            >
                Edit
            </button>
        );
    };

    const onPaymentNumberRender = (e) => {
        return e.rowIndex + 1;
    };

    const onExitByRender = (e) => {
        return RepaymentExitByTypeOptions.find((a) => a.id === e.data.exitByTypeId)?.name;
    };

    const renderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return new Date(e.value).toLocaleDateString('en-us');
    };

    if (!propertyId || !repayments) {
        return null;
    }

    return (
        <>
            <h2
                className='decorated'
                style={styles.sectionHeader}
            >
                Repayments
            </h2>
            <DataGrid
                dataSource={repayments}
                showBorders={true}
                showRowLines={false}
                showColumnHeaders={true}
                rowAlternationEnabled={true}
                columnMinWidth={50}
                columnAutoWidth={false}
                allowSorting={false}
                noDataText='No repayments on this property'
            >
                <Column
                    cellRender={onPaymentNumberRender}
                    caption='#'
                    width={20}
                />
                <Column
                    dataField='payDate'
                    caption='Date'
                    width={100}
                    cellRender={renderDate}
                />
                <Column
                    dataField='amount'
                    caption='Amount Paid'
                    dataType='number'
                    format='currency'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='remainingPrincipal'
                    caption='Remaining'
                    dataType='number'
                    format='currency'
                    width={100}
                    alignment='left'
                />
                <Column
                    caption='Exit By'
                    width={100}
                    alignment='left'
                    cellRender={onExitByRender}
                />
                <Column
                    dataField='salePrice'
                    caption='Price'
                    dataType='number'
                    format='currency'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='refiLenderName'
                    caption='New Lender'
                    width={250}
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                />
                <Column
                    alignment='center'
                    cellRender={actionCellRender}
                    width={60}
                    allowSorting={false}
                    allowResizing={false}
                    visible={editable && isEditable}
                />
            </DataGrid>
        </>
    );
};
